import React from 'react'
import { TextInputField } from '../../../../components/common/FormFields'

export interface SearchOption {
  key: string
  label: string
}

export interface FieldConfig {
  id: number
  label: string
  name: string
  placeholder?: string
  rowGroup?: string
  containerClass?: string
  /** Shows a red mandatory asterisk after the label. */
  required?: boolean
}

export interface SearchFormProps {
  searchOptions: SearchOption[]
  fieldsByMode: Record<string, string[]>
  fieldMeta: Record<string, FieldConfig>
  values: Record<string, string>
  errors?: Record<string, string>
  searchBy: string
  onSearchTypeChange: (type: string) => void
  onFieldChange: (fieldName: string, value: string) => void
  onSearch: (e: React.FormEvent) => void
  isLoading?: boolean
  hideLabel?: boolean
  columnLayout?: boolean
  formId?: string // Unique identifier for this form instance
  /** Button variant for the Search submit button. Defaults to 'primary'. */
  submitButtonVariant?: 'primary' | 'secondary'
}

const SearchForm: React.FC<SearchFormProps> = ({
  searchOptions,
  fieldsByMode,
  fieldMeta,
  values,
  errors = {},
  searchBy,
  onSearchTypeChange,
  onFieldChange,
  onSearch,
  isLoading = false,
  hideLabel = false,
  columnLayout = true,
  formId = 'searchForm',
  submitButtonVariant = 'primary',
}) => {
  const getGroupedFields = (mode: string) => {
    const fields = fieldsByMode[mode] || []
    const groups: Map<string, string[]> = new Map()
    fields.forEach((fid) => {
      const meta = fieldMeta[fid]
      const groupKey = meta?.rowGroup || fid
      if (!groups.has(groupKey)) groups.set(groupKey, [])
      groups.get(groupKey)!.push(fid)
    })
    return Array.from(groups.values())
  }

  return (
    <>
      {!hideLabel && <p className="p4 -medium mb-0! mt-5!">Search By:</p>}
      
      <div
        role="radiogroup"
        aria-label="Search by"
        className={`mt-2 flex ${columnLayout ? 'flex-col' : 'flex-row'} gap-3 cursor-pointer!`}
      >
        {searchOptions.map((opt) => (
          <label key={opt.key} htmlFor={`${formId}-radio-${opt.key}`} className="p3 -book cursor-pointer flex items-center gap-2">
            <input
              id={`${formId}-radio-${opt.key}`}
              type="radio"
              name={`${formId}-searchBy`}
              value={opt.key}
              checked={searchBy === opt.key}
              onChange={(e) => onSearchTypeChange(e.target.value)}
              aria-checked={searchBy === opt.key}
              className="w-4 h-4 cursor-pointer"
              style={{
                accentColor: '#2563eb',
              }}
              disabled={isLoading}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>

      <form onSubmit={onSearch} className="mt-6!">
        {getGroupedFields(searchBy).map((group, idx) => (
          <div
            key={`group-${idx}-${group.join('-')}`}
            className={group.length > 1 ? 'mt-1! flex flex-wrap gap-1 mb-4' : 'mt-2 mb-4'}
          >
            {group.map((fid) => (
              <div key={fieldMeta[fid]?.id ?? fid} className={fieldMeta[fid]?.containerClass || ''}>
                <TextInputField
                  label={
                    fieldMeta[fid]?.required ? (
                      <label htmlFor={fid} className="p4 -medium">
                        {fieldMeta[fid]?.label} <span className="text-red-600">*</span>
                      </label>
                    ) : (
                      fieldMeta[fid]?.label
                    )
                  }
                  id={fid}
                  name={fieldMeta[fid]?.name}
                  value={values[fid] || ''}
                  onChange={(e) => onFieldChange(fid, e.target.value)}
                  placeholder={fieldMeta[fid]?.placeholder}
                  customStyles={{ width: '350px', backgroundColor: '#FFF' }}
                  error={errors[fid]}
                  // Only widen the error-text boundary for a field alone in its
                  // row (NUID, Group ID). First/Last Name share a row side by
                  // side, so widening both would make their error text overlap.
                  errorWrapperWidth={group.length === 1 ? '500px' : undefined}
                />
              </div>
            ))}
          </div>
        ))}

        <button type="submit" className={`button -${submitButtonVariant} -light mt-4!`} disabled={isLoading}>
          Search
        </button>
      </form>
    </>
  )
}

export default SearchForm
